/**
 * Dashboard type definitions for Creative Analytics Dashboard (Phase 28).
 * Covers all 5 tabs: Verdicts, Gates, Insights, Creatives, Reports.
 *
 * These types represent the API response shapes and filter state
 * consumed by DashboardContext, React Query hooks, and tab components.
 */

// ============================================================================
// TAB & FILTER TYPES
// ============================================================================

export type DashboardTab = 'verdicts' | 'gates' | 'insights' | 'creatives' | 'reports';

export type InsightsSubView = 'drivers' | 'composition' | 'calibration' | 'explorer';

export interface DashboardFilters {
  templateId: string;
  projectId: string | 'all';
  period: '7d' | '30d' | '90d' | 'custom';
  fromDate?: string;   // ISO date string, required when period='custom'
  toDate?: string;     // ISO date string, required when period='custom'
  mediaType: 'all' | 'video' | 'image';
  activeTab: DashboardTab;
  insightsSubView?: InsightsSubView;
  drilldownGate?: string;
  drilldownVerdict?: string;
  drilldownAttribute?: string;
}

// ============================================================================
// COMMON DATA FIELDS (injected by backend into all dashboard data payloads)
// ============================================================================

/** All dashboard API data payloads include a dataTimestamp for freshness display. */
export interface WithDataTimestamp {
  dataTimestamp?: string;
}

// ============================================================================
// VERDICT TYPES
// ============================================================================

export interface VerdictCard {
  verdict: 'Scale' | 'Fix' | 'Kill';
  count: number;
  percentage: number;
  trendDelta: number;
  sparkline: number[];
}

export interface VerdictCharacteristic {
  type: 'gate' | 'dimension' | 'taxonomy' | 'kpi' | 'placement' | 'mediaType';
  label: string;
  prevalence: number;
  fieldName: string;
  value: string;
}

export interface ProfileCreative {
  creativeId: string;
  name: string;
  mediaType: 'video' | 'image';
  reason: string;
}

export interface VerdictProfile {
  verdict: 'Scale' | 'Fix' | 'Kill';
  characteristics: VerdictCharacteristic[];
  topCreatives: ProfileCreative[];
}

export interface ProjectComparisonRow {
  projectId: string;
  projectName: string;
  verdictBar: { scale: number; fix: number; kill: number };
  primaryKpi: { label: string; value: number; unit: string; trend: number | null };
}

export interface KpiSummaryCard {
  kpiId: string;
  label: string;
  unit: string;
  value: number;
  delta: number;
  percentile: number;
  higherIsBetter: boolean;
  isPrimary: boolean;
}

export interface VerdictMovement {
  creativeId: string;
  creativeName: string;
  from: 'Scale' | 'Fix' | 'Kill';
  to: 'Scale' | 'Fix' | 'Kill';
}

export interface VerdictByPlacement {
  placement: string;
  scale: number;
  fix: number;
  kill: number;
}

/** Verdict change entry — overnight verdict transitions (Phase 29) */
export interface VerdictChange {
  creativeId: string;
  creativeName: string;
  from: 'Scale' | 'Fix' | 'Kill';
  to: 'Scale' | 'Fix' | 'Kill';
  ruleLabel: string;
  timestamp: string;
}

/** Backend-resolved KPI config (Phase 999.1) — surfaces source of primary KPI selection. */
export interface ResolvedKpiConfig {
  primaryKpiId: string;
  secondaryKpiIds: string[];
  source: 'auto-goal' | 'auto-fallback' | 'manual';
}

export interface VerdictSummary extends WithDataTimestamp {
  verdictCards: VerdictCard[];
  trendData: { date: string; scale: number; fix: number; kill: number }[];
  projectComparison: ProjectComparisonRow[];
  kpiSummary: KpiSummaryCard[];
  verdictByPlacement: VerdictByPlacement[];
  keyMovements: VerdictMovement[];
  verdictProfiles: VerdictProfile[];
  verdictChanges?: VerdictChange[];
  resolvedKpiConfig: ResolvedKpiConfig;
}

// ============================================================================
// GATE TYPES
// ============================================================================

export interface FailingCreative {
  creativeId: string;
  name: string;
  mediaType: 'video' | 'image';
}

export interface GateStats {
  gateId: string;
  name: string;
  role: 'critical' | 'standard';
  passRate: number;
  passCount: number;
  failCount: number;
  trend: number;
  byPlacement: Record<string, number>;
  byObjective: Record<string, number>;
  failingCreatives: FailingCreative[];
  passingCreatives: FailingCreative[];
}

export type HeatmapColumnGroup = 'placement' | 'objective' | 'dimension' | 'taxonomy' | 'all';

export interface HeatmapRow {
  gateId: string;
  gateName: string;
  cells: Record<string, number>;
}

export interface HeatmapData {
  rows: HeatmapRow[];
  columns: string[];
  columnGroup: HeatmapColumnGroup;
}

export interface GateSummary extends WithDataTimestamp {
  gates: GateStats[];
  heatmapData: HeatmapData;
}

// ============================================================================
// INSIGHTS TYPES — KPI DRIVERS
// ============================================================================

export interface KpiDriver {
  type: 'dimension' | 'taxonomy' | 'gate' | 'placement';
  fieldName: string;
  value: string;
  label?: string;
  kpiValue: number;
  creativeCount: number;
  effectSize: number;
}

export interface KpiDriverGroup extends WithDataTimestamp {
  kpiId: string;
  kpiLabel: string;
  kpiUnit: string;
  portfolioAvg: number;
  higherIsBetter: boolean;
  drivers: KpiDriver[];
  totalCreatives?: number;
  creativesWithData?: number;
}

// ============================================================================
// INSIGHTS TYPES — COMPOSITION
// ============================================================================

export interface CompositionSegment {
  fieldName: string;
  value: string;
  share: number;
  kpiValue: number;
  creativeCount: number;
  smallSample: boolean;
}

/**
 * GroupField — what the user chose to group composition by.
 * Phase 33: replaces the old colliding `mode` param.
 */
export type GroupFieldKind = 'dimension' | 'taxonomy' | 'placement' | 'creative';

export interface GroupField {
  kind: GroupFieldKind;
  id: string; // diagnosis dimensionId, taxonomy fieldId, or literal 'placement'
}

export interface GroupFieldWithLabel extends GroupField {
  label: string;
}

export type ShareBasis = 'count' | 'spend';
export type ShareDenominatorMode = 'creatives' | 'segment-memberships';

export interface CompositionData extends WithDataTimestamp {
  segments: CompositionSegment[];
  portfolioAvgShare: number;
  portfolioAvgKpi: number;
  kpiId: string;
  kpiLabel: string;
  higherIsBetter: boolean;
  hasSpendData: boolean;
  // Phase 33 additions
  groupField: GroupFieldWithLabel;
  shareBasis: ShareBasis;
  shareDenominatorMode: ShareDenominatorMode;
}

// ============================================================================
// INSIGHTS TYPES — CALIBRATION
// ============================================================================

export interface CalibrationPoint {
  creativeId: string;
  creativeName: string;
  gatePassRate: number;
  kpiValue: number;
  verdict: 'Scale' | 'Fix' | 'Kill';
}

export interface CalibrationData extends WithDataTimestamp {
  points: CalibrationPoint[];
  rSquared: number;
  trendLine: { slope: number; intercept: number };
  disclaimer: string;
  xAxisLabel: string;
  kpiLabel: string;
}

// ============================================================================
// INSIGHTS TYPES — EXPLORER
// ============================================================================

export interface ExplorerPoint {
  creativeId: string;
  creativeName: string;
  x: number | string;
  y: number;
  verdict: 'Scale' | 'Fix' | 'Kill';
  mediaType: 'video' | 'image';
  colorValue?: string;
}

export interface ExplorerData extends WithDataTimestamp {
  points: ExplorerPoint[];
  xLabel: string;
  yLabel: string;
  xType: 'scale' | 'binary' | 'options' | 'taxonomy';
  vizType: 'scatter' | 'strip';
}

// ============================================================================
// CREATIVES TYPES
// ============================================================================

export interface DashboardCreative {
  creativeId: string;
  name: string;
  mediaType: 'video' | 'image';
  verdict: 'Scale' | 'Fix' | 'Kill';
  projectName: string;
  kpis: Record<string, number | null>;
  dimensions: Record<string, string | number | boolean | null>;
  taxonomy: Record<string, string | null>;
  gates: Record<string, boolean>;
}

export interface CreativesListResponse extends WithDataTimestamp {
  creatives: DashboardCreative[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================================================
// REPORTS TYPES
// ============================================================================

export interface ReportConfig {
  name: string;
  /**
   * Template id scoping this report. Phase 37 D-03: threaded into
   * preview/save/export body so the backend LabelContext resolves
   * template-defined dim/gate names. Optional for backward-compat with
   * legacy saved reports that predate this field (they fall back to
   * camel_to_title per D-05).
   */
  templateId?: string;
  rowDimensions: string[];
  columnDimensions: string[];
  metrics: string[];
  period?: '7d' | '30d' | '90d' | 'custom' | 'all';
  fromDate?: string;
  toDate?: string;
}

export interface ReportDimensionItem {
  id: string;
  label: string;
}

export interface ReportDimensionGroup {
  category: string;
  items: ReportDimensionItem[];
}

export interface ReportOptions {
  dimensions: ReportDimensionGroup[];
  metrics: { category: string; items: ReportDimensionItem[] | string }[];
}

export interface SavedReport {
  reportId: string;
  name: string;
  /** Phase 37 D-03: persisted from save so export resolves template labels; optional for legacy items. */
  templateId?: string;
  rowDimensions: string[];
  columnDimensions: string[];
  metrics: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportPreviewData {
  headers: string[];
  rows: (string | number | null)[][];
  totals?: (string | number | null)[] | null;
}

export interface ReportActivityNotice {
  kind: 'noMetaDailyActivity';
  title: string;
  message: string;
  checkedCreativeCount: number;
  dailyRowCount: number;
  fromDate: string;
  toDate: string;
  zeroFilled: boolean;
}

/**
 * Phase 37 Plan 04 (D-11, D-13): preview response meta envelope.
 * totalRowCount = engine row count before capping.
 * capApplied = true when server truncated data.rows to PREVIEW_CAP (200).
 * warning = present when 5000 <= totalRowCount <= 10000.
 * activityNotice = optional service-produced context for explicit no-activity states.
 */
export interface ReportPreviewMeta {
  totalRowCount: number;
  capApplied: boolean;
  warning?: string;
  activityNotice?: ReportActivityNotice;
}

/**
 * Phase 37 Plan 04: full preview response envelope (data + meta).
 * Kept as a distinct type so callers that still need just the table
 * payload can use ReportPreviewData directly.
 */
export interface ReportPreviewResponse {
  data: ReportPreviewData;
  meta: ReportPreviewMeta;
}

/**
 * Phase 37 Plan 04 (D-12): structured 413 body when export exceeds 10K rows.
 * Thrown as a typed error by exportReportCsv so the UI can surface the server
 * guidance message verbatim.
 */
export interface ReportExportError {
  code: 'REPORT_TOO_LARGE';
  message: string;
  totalRows: number;
  cap: number;
}

/**
 * Runtime-thrown error class for export failures. Carries the structured body
 * so UI handlers can read .code / .message / .totalRows without re-parsing.
 */
export class ReportTooLargeError extends Error {
  readonly code = 'REPORT_TOO_LARGE' as const;
  readonly totalRows: number;
  readonly cap: number;
  constructor(body: ReportExportError) {
    super(body.message);
    this.name = 'ReportTooLargeError';
    this.totalRows = body.totalRows;
    this.cap = body.cap;
  }
}

// ============================================================================
// EDGE STATE
// ============================================================================

export interface DashboardEdgeState {
  isEmpty: boolean;
  hasNoMetaMetrics: boolean;
  hasMixedMetrics: boolean;
  metaCreativeCount: number;
  totalCreativeCount: number;
  hasNoCustomGates: boolean;
  hasNoDimensions: boolean;
  isSmallSample: boolean;
  isTooSmallForR2: boolean;
  isLegacyTemplate: boolean;
  hasTemplateChangeMidPortfolio: boolean;
  templateChangedCount: number;
  allSameVerdict: boolean;
}

// ============================================================================
// API ENVELOPE
// ============================================================================

export interface DashboardApiResponse<T> {
  data: T;
  meta: {
    teamId: string;
    templateId: string;
    period: string;
    timestamp: string;
    edgeState: DashboardEdgeState;
  };
}

// ============================================================================
// META KPI CATALOG — All aggregatable Meta Ads KPIs (Phase 30 registry sync)
// ============================================================================

export interface MetaKpi {
  id: string;
  label: string;
  unit: string;
  higherIsBetter: boolean;
}

export const META_KPI_CATALOG: MetaKpi[] = [
  // Delivery (6)
  { id: 'spend', label: 'Spend', unit: '$', higherIsBetter: false },
  { id: 'impressions', label: 'Impressions', unit: '#', higherIsBetter: true },
  { id: 'reach', label: 'Reach', unit: '#', higherIsBetter: true },
  { id: 'frequency', label: 'Frequency', unit: 'x', higherIsBetter: false },
  { id: 'cpm', label: 'CPM', unit: '$', higherIsBetter: false },
  { id: 'cpc', label: 'Cost per Click', unit: '$', higherIsBetter: false },

  // Traffic (7)
  { id: 'clicks', label: 'Clicks (All)', unit: '#', higherIsBetter: true },
  { id: 'ctr', label: 'Click-Through Rate', unit: '%', higherIsBetter: true },
  { id: 'linkClicks', label: 'Link Clicks', unit: '#', higherIsBetter: true },
  { id: 'linkCtr', label: 'Link Click-Through Rate', unit: '%', higherIsBetter: true },
  { id: 'outboundClicks', label: 'Outbound Clicks', unit: '#', higherIsBetter: true },
  { id: 'outboundCtr', label: 'Outbound CTR', unit: '%', higherIsBetter: true },
  { id: 'landingPageViews', label: 'Landing Page Views', unit: '#', higherIsBetter: true },

  // Engagement (6)
  { id: 'postEngagements', label: 'Post Engagements', unit: '#', higherIsBetter: true },
  { id: 'reactions', label: 'Reactions', unit: '#', higherIsBetter: true },
  { id: 'comments', label: 'Comments', unit: '#', higherIsBetter: true },
  { id: 'shares', label: 'Shares', unit: '#', higherIsBetter: true },
  { id: 'engagementRate', label: 'Engagement Rate', unit: '%', higherIsBetter: true },
  { id: 'saves', label: 'Saves', unit: '#', higherIsBetter: true },

  // Video (10)
  { id: 'videoViews', label: 'Video Views', unit: '#', higherIsBetter: true },
  { id: 'video3sViews', label: '3-Second Video Views', unit: '#', higherIsBetter: true },
  { id: 'video25Percent', label: 'Video Views (25%)', unit: '#', higherIsBetter: true },
  { id: 'video50Percent', label: 'Video Views (50%)', unit: '#', higherIsBetter: true },
  { id: 'video75Percent', label: 'Video Views (75%)', unit: '#', higherIsBetter: true },
  { id: 'video95Percent', label: 'Video Views (95%)', unit: '#', higherIsBetter: true },
  { id: 'thruPlays', label: 'ThruPlays', unit: '#', higherIsBetter: true },
  { id: 'videoCompletionRate', label: 'Video Completion Rate', unit: '%', higherIsBetter: true },
  { id: 'avgWatchTime', label: 'Average Watch Time', unit: 's', higherIsBetter: true },
  { id: 'thruPlayRate', label: 'ThruPlay Rate', unit: '%', higherIsBetter: true },

  // Conversion (8)
  { id: 'purchases', label: 'Purchases', unit: '#', higherIsBetter: true },
  { id: 'purchaseValue', label: 'Purchase Value', unit: '$', higherIsBetter: true },
  { id: 'viewContent', label: 'View Content', unit: '#', higherIsBetter: true },
  { id: 'addToCart', label: 'Add to Cart', unit: '#', higherIsBetter: true },
  { id: 'initiateCheckout', label: 'Initiate Checkout', unit: '#', higherIsBetter: true },
  { id: 'cpa', label: 'Cost per Action', unit: '$', higherIsBetter: false },
  { id: 'roas', label: 'Return on Ad Spend', unit: 'x', higherIsBetter: true },
  { id: 'costPerPurchase', label: 'Cost per Purchase', unit: '$', higherIsBetter: false },

  // Cost (5)
  { id: 'costPerThruplay', label: 'Cost per ThruPlay', unit: '$', higherIsBetter: false },
  { id: 'costPerLinkClick', label: 'Cost per Link Click', unit: '$', higherIsBetter: false },
  { id: 'costPerLandingPageView', label: 'Cost per Landing Page View', unit: '$', higherIsBetter: false },
  { id: 'costPerEngagement', label: 'Cost per Engagement', unit: '$', higherIsBetter: false },
  { id: 'costPerAddToCart', label: 'Cost per Add to Cart', unit: '$', higherIsBetter: false },
] as const;
